import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import {
  clearPasswordRecoveryInProgress,
  getPasswordResetRedirectTo,
  markPasswordRecoveryInProgress,
} from "@/lib/password-recovery";
import { clearSessionData } from "@/lib/session-storage";

type AppRole = "student" | "employer" | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    role: "student" | "employer",
    metadata?: Record<string, unknown>
  ) => Promise<{ error: Error | null; success: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; role: AppRole }>;
  signOut: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const extractRoleFromMetadata = (metadata: unknown): AppRole => {
  const role = (metadata as { role?: unknown } | null | undefined)?.role;
  return role === "student" || role === "employer" ? role : null;
};

const translateAuthError = (error: AuthError | Error): string => {
  const msg = error.message.toLowerCase();
  if (msg.includes("invalid login credentials")) return "Email ou mot de passe incorrect";
  if (msg.includes("email not confirmed")) return "Veuillez confirmer votre email avant de vous connecter";
  if (msg.includes("user already registered")) return "Cet email est déjà utilisé";
  if (msg.includes("password") && msg.includes("weak")) return "Le mot de passe est trop faible";
  if (msg.includes("rate limit")) return "Trop de tentatives. Veuillez patienter quelques minutes.";
  if (msg.includes("network")) return "Erreur réseau. Vérifiez votre connexion.";
  return error.message;
};

const resolveUserRole = async (user: User): Promise<AppRole> => {
  // 1) RPC (source de vérité)
  try {
    const { data, error } = await supabase.rpc("get_user_role", { _user_id: user.id });
    if (!error && (data === "student" || data === "employer")) {
      return data;
    }
  } catch {
    // ignore
  }

  // 2) Lecture directe (fallback)
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("role")
      .eq("id", user.id)
      .maybeSingle();

    if (!error && (data?.role === "student" || data?.role === "employer")) {
      return data.role;
    }
  } catch {
    // ignore
  }

  // 3) Metadata (dernier recours)
  return extractRoleFromMetadata(user.user_metadata);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole>(null);
  const [loading, setLoading] = useState(true);

  // Évite les retours d’appels async obsolètes
  const roleRequestId = useRef(0);

  const applySession = useCallback(async (nextSession: Session | null) => {
    const requestId = ++roleRequestId.current;

    setSession(nextSession);
    setUser(nextSession?.user ?? null);

    if (!nextSession?.user) {
      setRole(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const resolvedRole = await resolveUserRole(nextSession.user);
    if (roleRequestId.current !== requestId) return;

    setRole(resolvedRole);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const safeApply = async (nextSession: Session | null) => {
      if (cancelled) return;
      await applySession(nextSession);
    };

    // Listener d’abord
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // Important security behavior:
      // Supabase creates a temporary session when clicking a password recovery email.
      // We mark that state so protected pages can be blocked until the password is changed.
      if (event === "PASSWORD_RECOVERY") {
        markPasswordRecoveryInProgress();
      }
      if (event === "SIGNED_OUT") {
        clearPasswordRecoveryInProgress();
      }
      void safeApply(nextSession);
    });

    // Puis session initiale
    supabase.auth.getSession().then(({ data, error }) => {
      if (error) {
        console.error("[Auth] getSession error:", error);
        void safeApply(null);
        return;
      }
      void safeApply(data.session);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [applySession]);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      userRole: "student" | "employer",
      metadata?: Record<string, unknown>
    ): Promise<{ error: Error | null; success: boolean }> => {
      try {
        const { error } = await supabase.auth.signUp({
          email: email.trim().toLowerCase(),
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: {
              ...metadata,
              role: userRole,
            },
          },
        });

        if (error) {
          return { error: new Error(translateAuthError(error)), success: false };
        }

        // Le listener onAuthStateChange mettra à jour user/session/role.
        return { error: null, success: true };
      } catch (error) {
        console.error("[Auth] signUp exception:", error);
        return { error: new Error("Une erreur inattendue s'est produite"), success: false };
      }
    },
    []
  );

  const signIn = useCallback(
    async (email: string, password: string): Promise<{ error: Error | null; role: AppRole }> => {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password,
        });

        if (error) {
          return { error: new Error(translateAuthError(error)), role: null };
        }

        const nextRole = data.user ? await resolveUserRole(data.user) : null;

        // Set immédiat (évite les flash/redirects avant le listener)
        setSession(data.session);
        setUser(data.user ?? null);
        setRole(nextRole);
        setLoading(false);

        return { error: null, role: nextRole };
      } catch (error) {
        console.error("[Auth] signIn exception:", error);
        return { error: new Error("Une erreur inattendue s'est produite"), role: null };
      }
    },
    []
  );

  const signOut = useCallback(async (): Promise<{ error: Error | null }> => {
    try {
      // UI immédiate
      setUser(null);
      setSession(null);
      setRole(null);
      
      // Clear session management data
      clearSessionData();

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("[Auth] signOut error:", error);
        return { error };
      }
      return { error: null };
    } catch (error) {
      console.error("[Auth] signOut exception:", error);
      return { error: error as Error };
    }
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<{ error: Error | null }> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: getPasswordResetRedirectTo(),
      });
      if (error) {
        return { error: new Error(translateAuthError(error)) };
      }
      return { error: null };
    } catch (error) {
      console.error("[Auth] resetPassword exception:", error);
      return { error: error as Error };
    }
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      session,
      role,
      loading,
      signUp,
      signIn,
      signOut,
      resetPassword,
    }),
    [user, session, role, loading, signUp, signIn, signOut, resetPassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

